import React from "react";
import CountryFlag from "../CountryFlag";
import { ConnectBody, ConnectFoot, PrimaryButton, SecondaryButton } from "./ConnectScreen";
import { t } from "../../i18n";
import { T } from "../../i18n/T";

// Step 2 of "Connect your bank", full screen (founder 2026-10-19): who will
// pay them, after the country.
//
//   Stripe    "You'll be paid with" + the big Stripe card
//   MassPay   the round flag, "{country} selected", and the MassPay card naming
//             the wallets we have for that country (founder: "keep the local
//             wallet data … show them here"), else "Your bank or e-wallet"
//   not yet   "Payouts aren't in {country} yet" + Notify me (their email goes on
//             that country's list)
//
// Partner marks: Stripe's official white wordmark, unmodified
// (public/stripe-wordmark-white.svg); MassPay's white wordmark from its own
// site (Carolina's OK to show it, 2026-10-13).

const BRAND = { stripe: "Stripe", masspay: "MassPay" };

export type RailCountry = { name: string; code?: string | null; wallets?: string[] };

const CheckLine = ({ children }: { children: React.ReactNode }) => (
  <p className="cx-ck">
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>
    {children}
  </p>
);

const ErrorLine = ({ error }: { error?: string }) => (error ? <p className="cx-error" role="alert">{error}</p> : null);

export function StripeRail(props: {
  country?: RailCountry | null;
  /** "· Change" after the country (back to the picker, or "Change your country?"). */
  onChange?: () => void;
  onContinue: () => void;
  pending?: boolean;
  error?: string;
}) {
  return (
    <>
      <ConnectBody center>
        <h2 id="cx-title" className="cx-title">{t("connectBank.youLlBePaidWith")}</h2>
        <div className="cx-stripe-card">
          <img className="cx-stripe-wm" src="/stripe-wordmark-white.svg" alt={BRAND.stripe} />
          <CheckLine>{t("connectBank.straightToYourBank")}</CheckLine>
          <CheckLine>{t("connectBank.bankDetailsStayWith")}</CheckLine>
          <CheckLine>{t("connectBank.takesAboutMinutes")}</CheckLine>
        </div>
        {props.country?.name && (
          <p className="cx-picked">
            <CountryFlag code={props.country.code} />
            <span>{props.country.name}</span>
            {props.onChange && (
              <>
                <span className="cx-picked-dot" aria-hidden="true">·</span>
                <button type="button" className="cx-picked-change" onClick={props.onChange} disabled={props.pending}>{t("connectBank.change")}</button>
              </>
            )}
          </p>
        )}
      </ConnectBody>
      <ConnectFoot>
        <ErrorLine error={props.error} />
        <PrimaryButton onClick={props.onContinue} disabled={props.pending} busy={props.pending}>
          {props.pending ? t("connectBankSheet.openingStripe") : t("connectBank.continueToStripe")}
        </PrimaryButton>
      </ConnectFoot>
    </>
  );
}

export function MasspayRail(props: {
  country: RailCountry;
  /** "Not in {country}? Change country". */
  onChange?: () => void;
  onContinue: () => void;
  pending?: boolean;
  error?: string;
}) {
  const wallets = (props.country.wallets || []).slice(0, 4);
  return (
    <>
      <ConnectBody center>
        <div className="cx-hero"><CountryFlag code={props.country.code} shape="circle" /></div>
        <h2 id="cx-title" className="cx-title cx-title-hero">{t("connectBank.countrySelected", { country: props.country.name })}</h2>
        <p className="cx-sub">{t("connectBank.youLlBePaidWithMasspay")}</p>
        <div className="cx-mp-card">
          <img className="cx-mp-wm" src="/masspay-wordmark-white.svg" alt={BRAND.masspay} />
          <p className="cx-mp-label">{t("connectBank.withdrawTo")}</p>
          {wallets.length > 0 ? (
            <div className="cx-chips">
              {wallets.map((w) => <span className="cx-chip" key={w}>{w}</span>)}
              <span className="cx-chip cx-chip-bank">{t("connectBank.yourBank")}</span>
            </div>
          ) : (
            <p className="cx-mp-plain">{t("connectBank.yourBankOrEWallet")}</p>
          )}
          <div className="cx-mp-div" />
          <CheckLine>{t("connectBank.idCheckAboutMinutes")}</CheckLine>
        </div>
        {props.onChange && (
          <button type="button" className="cx-link" onClick={props.onChange} disabled={props.pending}>
            {t("connectBankSheet.notInChangeCountry", { country: props.country.name })}
          </button>
        )}
      </ConnectBody>
      <ConnectFoot>
        <ErrorLine error={props.error} />
        <PrimaryButton onClick={props.onContinue} disabled={props.pending} busy={props.pending}>{t("connectBankSheet.continue")}</PrimaryButton>
      </ConnectFoot>
    </>
  );
}

export function NotYetRail(props: { country: RailCountry; onNotify: () => void; onOther: () => void; pending?: boolean; error?: string }) {
  return (
    <>
      <ConnectBody center>
        <div className="cx-hero is-muted"><CountryFlag code={props.country.code} shape="circle" /></div>
        <h2 id="cx-title" className="cx-title cx-title-hero">{t("connectBank.payoutsArenTInYet", { country: props.country.name })}</h2>
        <p className="cx-sub"><T k="connectBank.weLlEmailYou" tags={{ 0: <br /> }} /></p>
      </ConnectBody>
      <ConnectFoot>
        <ErrorLine error={props.error} />
        <PrimaryButton onClick={props.onNotify} disabled={props.pending} busy={props.pending}>{t("connectBank.notifyMe")}</PrimaryButton>
        <SecondaryButton onClick={props.onOther} disabled={props.pending}>{t("connectBank.chooseAnotherCountry")}</SecondaryButton>
      </ConnectFoot>
    </>
  );
}
